import React, { useEffect, useState } from "react";
import {
  Link,
  LinkSizeEnum,
} from "../../../../../../components/core/Link/Link";
import { ReactComponent as AddIcon } from "../../../../../../icons/add.svg";
import {
  AlignmentEnum,
  FontWeightEnum,
} from "../../../../../../types/AppTypes";
import {
  CATEGORY_LABEL,
  CERTIFICATION_CERTIFICATE_LABEL,
  CERTIFICATION_LABEL,
  CERTIFICATIONS_LABEL,
  DELETE_CERTIFICATION_BODY_LABEL_1,
  DELETE_CERTIFICATION_LABEL,
  DESCRIPTION_LABEL,
  EMPTY_CERTIFICATIONS_MESSAGE,
  EXECUTION_MODE_LABEL,
  NAME_LABEL,
  STATE_LABEL,
  URL_LABEL,
} from "../../../../../../constants/Dictionary";
import SearchInput from "../../../../../../components/core/SearchInput/SearchInput";
import { DoubleStructure } from "../../../../../../components/core/DoubleStructure/DoubleStructure";
import { BodyTyp } from "../../../../../../components/Typography/Typography";
import { StyledExpandTable } from "../../ProfessionalHistory.styled";
import { Loader } from "../../../../../../components/core/Loader/Loader";
import { EmptyState } from "../../../../../../components/EmptyState/EmptyState";
import {
  Button,
  ButtonVariants,
} from "../../../../../../components/core/Button/Button";
import { PopupConfirm } from "../../../../../../components/PopupConfirm/PopupConfirm";
import emptyCertificationsImage from "../../../../../../images/certificates.png";
import { HistoryCertificationModal } from "./HistoryCertificationModal/HistoryCertificationModal";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../../components/core/Select/Select";
import { COLLABORATORS_PAGE_SIZE } from "../../../../../../constants/AppConstants";
import {
  CollaboratorCmSheet,
  CollaboratorHistoryCertification,
  CollaboratorDetails,
} from "../../../../../../types/api/collaborators.type";
import { useDebounce } from "use-debounce";
import CollaboratorsHistoryCertificateOfCertificationsService from "../../../../../../services/api/collaborators/CollaboratorsHistoryCertificateOfCertfications.service";
import { getCollaboratorHistoryCertificationsColumns } from "../../../../../../constants/TableColumns";
import { CertificateModal } from "../../../../../../components/CertificateModal/CertificateModal";
import { useAppSelector } from "../../../../../../hooks/store.hook";
import { findLabelFromValue } from "../../../../../../utils/orgFunctions.utils";
import { TextBoxGrid } from "../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import {
  Table,
  TableThemeEnum,
} from "../../../../../../components/core/Table/Table";
import LoadingThumbnail from "../../../../../../components/LoadingThumbnail/LoadingThumbnail";
import { canEditCollaboratorHistory } from "../../../../../../utils/canEditCollaboratorHistory.utils";
import {
  useCertificationHistory,
  useCertificationHistoryDelete,
} from "../../../../../../hooks/formations.hook";

type CertificationsHistoryProps = {
  setLink: (component: React.ReactNode) => void;
  onStatusRefresh: () => void;
  collaborator: CollaboratorDetails;
  isMyProfile?: boolean;
  currentCmSheet?: CollaboratorCmSheet;
};

export const CertificationsHistory = ({
  setLink,
  onStatusRefresh,
  collaborator,
  isMyProfile,
  currentCmSheet,
}: CertificationsHistoryProps) => {
  const [selectedCertification, setSelectedCertification] =
    useState<CollaboratorHistoryCertification | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);

  const { profile } = useAppSelector((state) => state.user);
  const { orgSettings } = useAppSelector((state) => state.config);
  const profileId = collaborator.id;

  const certificateApi = (certificationId: number) =>
    CollaboratorsHistoryCertificateOfCertificationsService.get(
      profileId,
      certificationId,
    );

  const {
    data: historyCertifications,
    isLoading: certificationsLoading,
    isFetching: certificationsFetching,
  } = useCertificationHistory({
    profileId,
    pageNumber,
    searchDebouncedValue,
    status,
  });
  const {
    mutateAsync: deleteCertification,
    isPending: pendingDeleteCertification,
  } = useCertificationHistoryDelete(profileId);

  const hasData =
    historyCertifications !== undefined
      ? historyCertifications.pagination.total > 0
      : null;

  const canEditHistory = canEditCollaboratorHistory(
    profile,
    collaborator,
    currentCmSheet,
    isMyProfile,
  );

  useEffect(() => {
    if (hasData && canEditHistory) {
      setLink(
        <Link
          onClick={() => setShowAddModal(true)}
          icon={<AddIcon />}
          size={LinkSizeEnum.SMALL}
        >
          {CERTIFICATION_LABEL}
        </Link>,
      );
    }
    return () => setLink(null);
  }, [hasData, canEditHistory]);

  const handleDeleteCertification = async () => {
    await deleteCertification(selectedCertification?.id!);
    hideDeletePopup();
    onStatusRefresh();
    setSelectedCertification(null);
    setShowDeleteModal(false);
  };

  const onEdit = (record: CollaboratorHistoryCertification) => {
    setSelectedCertification(record);
    setShowAddModal(true);
  };

  const hideAddModal = () => {
    setSelectedCertification(null);
    setShowAddModal(false);
  };

  const handleSave = () => {
    hideAddModal();
    onStatusRefresh();
  };

  const onDelete = (record: CollaboratorHistoryCertification) => {
    setSelectedCertification(record);
    setShowDeleteModal(true);
  };

  const hideDeletePopup = () => {
    setSelectedCertification(null);
    setShowDeleteModal(false);
  };

  const onView = (record: CollaboratorHistoryCertification) => {
    setSelectedCertification(record);
    setShowViewModal(true);
  };

  const expandedRow = (
    record: CollaboratorHistoryCertification,
  ): React.ReactElement | null => {
    return (
      <TextBoxGrid
        numCol={3}
        items={[
          {
            label: CATEGORY_LABEL,
            text: findLabelFromValue(
              orgSettings.certifications.categories,
              record.certification.category,
            ),
          },
          {
            label: EXECUTION_MODE_LABEL,
            text: findLabelFromValue(
              orgSettings.certifications.executionModes,
              record.certification.executionMode,
            ),
          },
          {
            label: URL_LABEL,
            text: record.certification.url,
          },
          {
            label: DESCRIPTION_LABEL,
            text: record.certification.description,
          },
        ]}
      />
    );
  };

  const hideDeleteIconFromCmSheet = (row: CollaboratorHistoryCertification) => {
    return row.fromCmSheet === false;
  };

  return (
    <>
      {certificationsLoading &&
      !status &&
      !searchDebouncedValue &&
      hasData === null ? (
        <Loader loading={true}>
          <LoadingThumbnail marginTop={316} />
        </Loader>
      ) : (
        <div>
          {!hasData && !status && !searchDebouncedValue ? (
            <div className="empty-state">
              <EmptyState
                label={EMPTY_CERTIFICATIONS_MESSAGE}
                imageSrc={emptyCertificationsImage}
              >
                {canEditHistory && (
                  <Button
                    label={CERTIFICATION_LABEL}
                    variant={ButtonVariants.SECONDARY}
                    icon={<AddIcon />}
                    onClick={() => {
                      setShowAddModal(true);
                    }}
                  />
                )}
              </EmptyState>
            </div>
          ) : (
            <>
              <DoubleStructure verticalAlignment={AlignmentEnum.CENTER}>
                <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                  {!certificationsFetching &&
                    `${historyCertifications?.pagination.total} ${historyCertifications?.pagination.total === 1 ? CERTIFICATION_LABEL : CERTIFICATIONS_LABEL}`}
                </BodyTyp>
                <div className="filters two-columns">
                  <SearchInput
                    onChange={setSearchValue}
                    label={NAME_LABEL}
                    name={"search"}
                    value={searchValue}
                  />
                  <Select
                    label={STATE_LABEL}
                    variant={SelectVariantEnum.SINGLE_SELECT}
                    onChange={setStatus}
                    options={orgSettings.certifications.status}
                    value={status}
                    allowClear={true}
                  />
                </div>
              </DoubleStructure>
              <StyledExpandTable>
                <Table<CollaboratorHistoryCertification>
                  theme={TableThemeEnum.DARK}
                  loading={certificationsFetching}
                  headers={getCollaboratorHistoryCertificationsColumns(
                    onView,
                    orgSettings,
                  )}
                  scrollY={"calc(100dvh - 502px)"}
                  onEdit={canEditHistory ? onEdit : undefined}
                  onDelete={canEditHistory ? onDelete : undefined}
                  rowHasDelete={hideDeleteIconFromCmSheet}
                  data={historyCertifications?.data || []}
                  pagination={{
                    current: pageNumber + 1,
                    onChange: (pageNumber) => setPageNumber(pageNumber - 1),
                    total: historyCertifications?.pagination.total ?? 0,
                    pageSize: COLLABORATORS_PAGE_SIZE,
                  }}
                  expandable={{
                    rowExpandable: (record) => !record.external,
                    expandedRowRender: expandedRow,
                  }}
                  emptyText={EMPTY_CERTIFICATIONS_MESSAGE}
                />
              </StyledExpandTable>
            </>
          )}
        </div>
      )}
      <PopupConfirm
        isOpen={showDeleteModal}
        label={DELETE_CERTIFICATION_LABEL}
        popupText={`${DELETE_CERTIFICATION_BODY_LABEL_1} ${selectedCertification?.certification.name}?`}
        onConfirm={() => {
          handleDeleteCertification();
        }}
        onCancel={() => {
          hideDeletePopup();
        }}
        isLoading={pendingDeleteCertification}
      />
      {showAddModal && (
        <HistoryCertificationModal
          certification={selectedCertification}
          isOpen={showAddModal}
          onSave={handleSave}
          onClose={() => hideAddModal()}
          collaborator={collaborator}
          certificateId={
            selectedCertification?.fileName
              ? selectedCertification.id
              : undefined
          }
        />
      )}
      <CertificateModal
        label={CERTIFICATION_CERTIFICATE_LABEL}
        isOpen={showViewModal}
        closeModal={() => setShowViewModal(false)}
        {...(selectedCertification && {
          certificateApi: () => certificateApi(selectedCertification.id),
        })}
        idKeyApi={`history-certification-${selectedCertification?.id}`}
      />
    </>
  );
};
